import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Clock, Coffee, CalendarDays, KeyRound } from "lucide-react";

const WEEKDAYS = [
  { value: 0, label: "Mon" },
  { value: 1, label: "Tue" },
  { value: 2, label: "Wed" },
  { value: 3, label: "Thu" },
  { value: 4, label: "Fri" },
  { value: 5, label: "Sat" },
  { value: 6, label: "Sun" },
];

export default function TherapistSettings({ onSaved }) {
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/therapist/me/settings")
      .then(({ data }) => setSettings(data))
      .catch(() => setSettings(false));
  }, []);

  if (settings === null) return <div className="text-mindora-muted">loading…</div>;
  if (settings === false) return <div className="text-mindora-ink">Could not load settings.</div>;

  const toggleDay = (d) => {
    const has = settings.working_days.includes(d);
    const next = has ? settings.working_days.filter((x) => x !== d) : [...settings.working_days, d].sort();
    setSettings({ ...settings, working_days: next });
  };

  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/therapist/me/settings", {
        working_days: settings.working_days,
        start_time: settings.start_time,
        end_time: settings.end_time,
        session_duration_min: Number(settings.session_duration_min),
        buffer_min: Number(settings.buffer_min),
      });
      setSettings(data);
      toast.success("Schedule preferences saved.");
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save");
    } finally {
      setBusy(false);
    }
  };

  // Compute preview slots client-side
  const previewSlots = computeSlots(settings);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10" data-testid="therapist-settings">
      <div className="lg:col-span-7 space-y-8">
        {/* Working days */}
        <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <CalendarDays size={14} /> Working days
          </div>
          <p className="text-xs text-mindora-muted mt-1.5">Days clients can book you. Toggle to include or remove.</p>
          <div className="mt-5 flex flex-wrap gap-2" data-testid="settings-weekdays">
            {WEEKDAYS.map((d) => {
              const on = settings.working_days.includes(d.value);
              return (
                <button
                  key={d.value}
                  onClick={() => toggleDay(d.value)}
                  data-testid={`settings-day-${d.value}`}
                  className={`rounded-full border px-5 py-2 text-sm tracking-wide transition-all ${
                    on
                      ? "bg-mindora-red text-mindora-bg border-mindora-red"
                      : "bg-mindora-bg border-mindora-line text-mindora-ink hover:border-mindora-red"
                  }`}
                >
                  {d.label}
                </button>
              );
            })}
          </div>
        </section>

        {/* Hours */}
        <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <Clock size={14} /> Working hours
          </div>
          <p className="text-xs text-mindora-muted mt-1.5">Times during the day you take sessions (24-hour).</p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Start</Label>
              <Input
                type="time"
                value={settings.start_time}
                onChange={(e) => setSettings({ ...settings, start_time: e.target.value })}
                data-testid="settings-start-time"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
            <div>
              <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">End</Label>
              <Input
                type="time"
                value={settings.end_time}
                onChange={(e) => setSettings({ ...settings, end_time: e.target.value })}
                data-testid="settings-end-time"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
          </div>
        </section>

        {/* Duration + buffer */}
        <section className="rounded-3xl border border-mindora-line bg-mindora-panel p-7 shadow-clay">
          <div className="flex items-center gap-2 text-mindora-red text-xs uppercase tracking-[0.22em]">
            <Coffee size={14} /> Session length & breathing room
          </div>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Session duration (min)</Label>
              <Input
                type="number"
                min="15"
                max="240"
                value={settings.session_duration_min}
                onChange={(e) => setSettings({ ...settings, session_duration_min: e.target.value })}
                data-testid="settings-duration"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
            <div>
              <Label className="text-mindora-ink/80 text-xs uppercase tracking-wider">Buffer between (min)</Label>
              <Input
                type="number"
                min="0"
                max="120"
                value={settings.buffer_min}
                onChange={(e) => setSettings({ ...settings, buffer_min: e.target.value })}
                data-testid="settings-buffer"
                className="mt-2 bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red"
              />
            </div>
          </div>
        </section>

        <Button
          onClick={save}
          disabled={busy}
          data-testid="settings-save"
          className="btn-primary rounded-full px-7 py-3 h-auto text-sm tracking-wide"
        >
          {busy ? "Saving…" : "Save schedule"}
        </Button>
        {settings.working_days.length === 0 && (
          <p className="text-[11px] text-mindora-muted italic mt-3 max-w-md leading-relaxed">
            With no weekdays selected, your auto-grid stays empty. Clients can only book the specific times you add under <b>Offered times</b> — or send you a custom request.
          </p>
        )}
      </div>

      {/* Preview */}
      <div className="lg:col-span-5">
        <section className="rounded-3xl border border-mindora-line bg-mindora-bg p-7 sticky top-24" data-testid="settings-preview">
          <div className="text-xs uppercase tracking-[0.22em] text-mindora-red">Preview</div>
          <h3 className="font-serif text-2xl text-mindora-ink mt-2 leading-tight">A typical working day</h3>
          <p className="text-xs text-mindora-muted mt-1">{previewSlots.length} session slot{previewSlots.length === 1 ? "" : "s"} based on your settings.</p>
          {previewSlots.length === 0 ? (
            <div className="mt-5 text-sm italic text-mindora-muted">No slots fit between your start and end times — try shortening the session or extending hours.</div>
          ) : (
            <div className="mt-5 grid grid-cols-2 gap-2.5">
              {previewSlots.map((s) => (
                <div key={s} className="rounded-xl border border-mindora-line bg-mindora-panel py-2.5 text-center font-serif text-lg text-mindora-ink">{s}</div>
              ))}
            </div>
          )}
          <p className="text-[11px] text-mindora-muted mt-5 italic">
            Updates here apply immediately — existing bookings are preserved even if you remove their weekday.
          </p>
        </section>

        <ChangePasswordCard />
      </div>
    </div>
  );
}

function ChangePasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (next !== confirm) { toast.error("New passwords don't match."); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: current, new_password: next });
      toast.success("Password updated. Use the new one next time you sign in.");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not update password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="rounded-3xl border border-mindora-line bg-mindora-bg p-7 mt-6" data-testid="settings-change-password">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-mindora-red">
        <KeyRound size={12} /> Change password
      </div>
      <h3 className="font-serif text-2xl text-mindora-ink mt-2 leading-tight">Pick something only you'd choose.</h3>
      <p className="text-xs text-mindora-muted mt-1 leading-relaxed">
        Replaces your starter password. Minimum 8 characters — a passphrase works beautifully.
      </p>
      <form onSubmit={submit} className="mt-5 space-y-3">
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-mindora-ink/80">Current password</Label>
          <Input
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            required
            data-testid="password-current"
            className="mt-1 bg-mindora-panel border-mindora-line focus-visible:ring-mindora-red"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-mindora-ink/80">New password</Label>
          <Input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={next}
            onChange={(e) => setNext(e.target.value)}
            required
            data-testid="password-new"
            className="mt-1 bg-mindora-panel border-mindora-line focus-visible:ring-mindora-red"
          />
        </div>
        <div>
          <Label className="text-[11px] uppercase tracking-wider text-mindora-ink/80">Confirm new password</Label>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
            data-testid="password-confirm"
            className="mt-1 bg-mindora-panel border-mindora-line focus-visible:ring-mindora-red"
          />
        </div>
        <Button
          type="submit"
          disabled={busy}
          data-testid="password-submit"
          className="btn-primary rounded-full h-11 px-6 mt-2"
        >
          {busy ? "Updating…" : "Update password"}
        </Button>
      </form>
    </section>
  );
}

function computeSlots(s) {
  try {
    const [sh, sm] = (s.start_time || "09:00").split(":").map(Number);
    const [eh, em] = (s.end_time || "17:00").split(":").map(Number);
    const start = sh * 60 + sm;
    const end = eh * 60 + em;
    const dur = Number(s.session_duration_min) || 50;
    const buf = Number(s.buffer_min) || 0;
    if (dur <= 0) return [];
    const out = [];
    let t = start;
    while (t + dur <= end) {
      out.push(`${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`);
      t += dur + buf;
    }
    return out;
  } catch (e) {
    return [];
  }
}
